import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithActivity } from '../utils/fetchWithActivity';
import { humanizeEnum } from '../utils/humanizeEnum';
import { POLL_INTERVAL_MS, POLL_TIMEOUT_MS } from '../utils/pollingConstants';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const DOCUMENT_KINDS = ['dec_page', 'renewal', 'quote', 'non_renewal_notice', 'endorsement', 'loss_run', 'audit', 'contract', 'other'];
const LINES_OF_BUSINESS = ['general_liability', 'property', 'workers_comp', 'commercial_auto', 'umbrella', 'cyber', 'epli', 'bop', 'homeowners', 'personal_auto', 'flood', 'other'];

export interface DocumentIntelligenceRow {
  documentId: string;
  extractionStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  documentKind: string | null;
  lineOfBusiness: string | null;
  carrier: string | null;
  policyNumber: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  correctedByUserId?: string | null;
  extractionError?: string | null;
}

interface DocumentIntelligenceChipProps {
  documentId: string;
  initial?: DocumentIntelligenceRow | null;
  compact?: boolean;
}

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const DocumentIntelligenceChip: React.FC<DocumentIntelligenceChipProps> = ({ documentId, initial = null, compact = false }) => {
  const { token } = useAuth();
  const [row, setRow] = useState<DocumentIntelligenceRow | null>(initial);
  const [loading, setLoading] = useState(!initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [form, setForm] = useState<Partial<DocumentIntelligenceRow>>({});
  const pollStartedAt = useRef<number>(Date.now());
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchIntelligence = async (): Promise<DocumentIntelligenceRow | 'none' | null> => {
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/intelligence/document/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) return 'none';
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  };

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const poll = React.useCallback(async () => {
    const result = await fetchIntelligence();
    if (!mountedRef.current) return;

    if (result === null) {
      setLoading(false);
      return;
    }
    if (result === 'none') {
      setRow(null);
      setLoading(false);
      return;
    }

    setRow(result);
    setLoading(false);

    const stillWorking = result.extractionStatus === 'pending' || result.extractionStatus === 'processing';
    if (stillWorking && Date.now() - pollStartedAt.current < POLL_TIMEOUT_MS) {
      pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, token]);

  useEffect(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollStartedAt.current = Date.now();
    poll();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const retryAnalysis = async () => {
    setRetrying(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/intelligence/extract/${documentId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setEditing(false);
        setLoading(true);
        pollStartedAt.current = Date.now();
        poll();
      }
    } finally {
      setRetrying(false);
    }
  };

  const [carrierOptions, setCarrierOptions] = useState<string[] | null>(null);

  const openEditor = () => {
    setForm({
      documentKind: row?.documentKind || '',
      lineOfBusiness: row?.lineOfBusiness || '',
      carrier: row?.carrier || '',
      policyNumber: row?.policyNumber || '',
      effectiveDate: row?.effectiveDate ? row.effectiveDate.slice(0, 10) : '',
      expirationDate: row?.expirationDate ? row.expirationDate.slice(0, 10) : '',
    });
    setEditing(true);
    // E12 — fetched once when the editor opens, not on mount (the chip
    // renders per document row).
    if (carrierOptions === null) {
      fetchWithActivity(`${API_BASE_URL}/api/intelligence/company/carriers`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : []))
        .then((carriers) => setCarrierOptions(carriers))
        .catch(() => setCarrierOptions([]));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/intelligence/document/${documentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          documentKind: form.documentKind || null,
          lineOfBusiness: form.lineOfBusiness || null,
          carrier: form.carrier || null,
          policyNumber: form.policyNumber || null,
          effectiveDate: form.effectiveDate || null,
          expirationDate: form.expirationDate || null,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setRow(updated);
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 animate-pulse">
        Analyzing…
      </span>
    );
  }

  if (row && (row.extractionStatus === 'pending' || row.extractionStatus === 'processing')) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 animate-pulse">
        Analyzing…
      </span>
    );
  }

  const isUnavailable = !row || row.extractionStatus === 'failed' || row.extractionStatus === 'skipped';
  const canRetry = row?.extractionStatus === 'failed';

  const parts: string[] = [];
  if (!isUnavailable && row) {
    if (row.documentKind) parts.push(humanizeEnum(row.documentKind));
    if (row.lineOfBusiness) parts.push(humanizeEnum(row.lineOfBusiness));
    if (!compact && row.carrier) parts.push(row.carrier);
    if (!compact && row.effectiveDate) parts.push(`eff ${formatDate(row.effectiveDate)}`);
    if (!compact && row.expirationDate) parts.push(`exp ${formatDate(row.expirationDate)}`);
  }

  if (!isUnavailable && parts.length === 0) return null;

  return (
    <div className="relative inline-block">
      {isUnavailable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openEditor();
          }}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
          title={row?.extractionError || 'Click to add details manually'}
        >
          Details needed — add manually
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openEditor();
          }}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
          title="Click to review or correct"
        >
          {row?.correctedByUserId && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
          )}
          {parts.join(' · ')}
        </button>
      )}

      {editing && (
        <div
          className="absolute z-50 top-full left-0 mt-2 w-72 p-4 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {isUnavailable && row?.extractionError && (
            <p className="text-xs text-amber-700 dark:text-amber-300 line-clamp-3 mb-3" title={row.extractionError}>
              ⚠ Analysis failed: {row.extractionError}
            </p>
          )}
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Document Kind</label>
              <select
                value={form.documentKind || ''}
                onChange={(e) => setForm((f) => ({ ...f, documentKind: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
              >
                <option value="">—</option>
                {DOCUMENT_KINDS.map((k) => (
                  <option key={k} value={k}>{humanizeEnum(k)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Line of Business</label>
              <select
                value={form.lineOfBusiness || ''}
                onChange={(e) => setForm((f) => ({ ...f, lineOfBusiness: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
              >
                <option value="">—</option>
                {LINES_OF_BUSINESS.map((l) => (
                  <option key={l} value={l}>{humanizeEnum(l)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Carrier</label>
              <input
                type="text"
                list={`carrier-options-${documentId}`}
                value={form.carrier || ''}
                onChange={(e) => setForm((f) => ({ ...f, carrier: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
              />
              <datalist id={`carrier-options-${documentId}`}>
                {(carrierOptions || []).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Policy Number</label>
              <input
                type="text"
                value={form.policyNumber || ''}
                onChange={(e) => setForm((f) => ({ ...f, policyNumber: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Effective</label>
                <input
                  type="date"
                  value={form.effectiveDate || ''}
                  onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-secondary dark:text-dark-text mb-1">Expiration</label>
                <input
                  type="date"
                  value={form.expirationDate || ''}
                  onChange={(e) => setForm((f) => ({ ...f, expirationDate: e.target.value }))}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-dark-bg text-secondary dark:text-dark-text"
                />
              </div>
            </div>
          </div>

          <div className={`flex items-center mt-4 ${canRetry ? 'justify-between' : 'justify-end'}`}>
            {canRetry && (
              <button
                type="button"
                onClick={retryAnalysis}
                disabled={retrying}
                className="px-3 py-1.5 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 disabled:opacity-50 transition-colors"
              >
                {retrying ? 'Retrying…' : 'Retry analysis'}
              </button>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentIntelligenceChip;
